import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const LiliChatWindow = ({ user, onClose }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(`lili-${user?.id}-${Date.now()}`);
  const [hasConsented, setHasConsented] = useState(false);
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasShownWelcome = useRef(false);
  const hasGeneratedSummary = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Show welcome and consent messages only once when chat opens
    if (user && !hasShownWelcome.current) {
      hasShownWelcome.current = true;
      
      // Initial welcome message
      setMessages([
        {
          type: 'lili',
          text: `Hello ${user?.first_name || 'there'}! 👋 I'm Lili, your personal concierge at The Haute Circle. I'm here to help you explore our luxury fractional ownership offerings.`,
          timestamp: new Date().toISOString()
        }
      ]);

      // Show consent prompt after a brief delay
      setTimeout(() => {
        setShowConsentPrompt(true);
        setMessages(prev => [...prev, {
          type: 'lili',
          text: `Before we continue, I'd like to inform you about our privacy practices:\n\n🔒 Our conversations may be recorded and analyzed to improve your experience and help us understand your preferences better.\n\n📊 We collect data such as your interests, questions, and interactions to provide you with personalized recommendations.\n\nDo you consent to this data collection?`,
          timestamp: new Date().toISOString(),
          requiresConsent: true
        }]);
      }, 1000);
    }

    // Focus input on mount
    inputRef.current?.focus();
  }, [user]);

  const handleConsent = async (consented) => {
    setHasConsented(consented);
    setShowConsentPrompt(false);

    if (consented) {
      setMessages(prev => [...prev, {
        type: 'user',
        text: 'Yes, I consent',
        timestamp: new Date().toISOString()
      }, {
        type: 'lili',
        text: `Thank you for your consent! 🙏 I'm ready to assist you. What brings you to The Haute Circle today?`,
        timestamp: new Date().toISOString()
      }]);
    } else {
      setMessages(prev => [...prev, {
        type: 'user',
        text: 'No, I do not consent',
        timestamp: new Date().toISOString()
      }, {
        type: 'lili',
        text: `I completely respect your privacy decision. 🤝 Your conversations will not be stored. However, I'm still here and happy to help you with any questions about The Haute Circle! How can I assist you today?`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleSignInClick = () => {
    // Close chat window and navigate to login
    onClose();
    navigate('/login');
  };

  const handleSignUpClick = () => {
    // Close chat window and navigate to signup
    onClose();
    navigate('/signup');
  };

  const handleCloseChat = async () => {
    // Generate summary before closing (only once per session)
    if (user && !hasGeneratedSummary.current && messages.length > 2) {
      hasGeneratedSummary.current = true;
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        // Generate and store summary
        await fetch(`${API_URL}/api/chat/lili/close-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            session_id: sessionId,
            consent_given: hasConsented
          })
        });
        
        console.log('Conversation summary generated');
      } catch (error) {
        console.error('Error generating summary:', error);
        // Don't block closing on error
      }
    }
    
    // Close the chat
    onClose();
  };

  // Check if user is logged in
  if (!user) {
    return (
      <div className="fixed bottom-6 right-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border-2 border-gray-300 mx-4 sm:mx-0">
        {/* Header with Lili Logo Centered */}
        <div className="p-6 text-white relative" style={{ background: 'linear-gradient(to right, #D4AF37, #C5A028)' }}>
          {/* Close Button - Top Right */}
          <button 
            onClick={handleCloseChat} 
            className="absolute top-4 right-4 p-2 rounded-full transition bg-white/20 hover:bg-white/30"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Lili Logo - Top Center */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 flex items-center justify-center mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-collective/artifacts/g86n9tir_Lili%20Logo.1-cutout.png" 
                alt="Lili Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h3 className="font-bold text-xl">Meet Lili</h3>
            <p className="text-sm text-white/90">Your AI Concierge</p>
          </div>
        </div>
        
        <div className="p-6 text-center">
          <p className="text-gray-700 mb-6 font-medium">Please sign in to chat with Lili, your personal luxury concierge.</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleSignInClick}
              style={{ background: 'linear-gradient(to right, #D4AF37, #C5A028)' }}
              className="w-full hover:opacity-90 text-white font-bold"
            >
              Sign In
            </Button>
            <Button
              onClick={handleSignUpClick}
              variant="outline"
              className="w-full border-2 border-gray-300 font-semibold"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || showConsentPrompt) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    setMessages(prev => [...prev, {
      type: 'user',
      text: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Only send to backend if user has consented
      if (hasConsented) {
        const response = await fetch(`${API_URL}/api/chat/lili`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            message: userMessage,
            session_id: sessionId,
            consent_given: hasConsented
          })
        });

        if (!response.ok) {
          throw new Error('Failed to get response from Lili');
        }

        const data = await response.json();

        // Add Lili's response to chat
        setMessages(prev => [...prev, {
          type: 'lili',
          text: data.response,
          timestamp: data.timestamp
        }]);
      } else {
        // Simple response without backend storage (no consent)
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate thinking
        
        setMessages(prev => [...prev, {
          type: 'lili',
          text: `I'm here to help! While I can't store our conversation, I can still answer your questions about The Haute Circle, our luxury assets, fractional ownership, and more. What would you like to know?`,
          timestamp: new Date().toISOString()
        }]);
      }

    } catch (error) {
      console.error('Error chatting with Lili:', error);
      toast.error('Sorry, I\'m having trouble connecting. Please try again.');
      
      // Add error message from Lili
      setMessages(prev => [...prev, {
        type: 'lili',
        text: 'I apologize, but I\'m experiencing some technical difficulties. Please try again in a moment, or feel free to contact our team directly via WhatsApp or phone.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border-2 border-gray-300 mx-4 sm:mx-0 md:max-w-md" style={{ maxHeight: '85vh' }}>
      {/* Header with Lili Logo Centered */}
      <div className="p-4 text-white relative" style={{ background: 'linear-gradient(to right, #D4AF37, #C5A028)' }}>
        {/* Close Button - Top Right */}
        <button 
          onClick={handleCloseChat} 
          className="absolute top-4 right-4 p-2 rounded-full transition bg-white/20 hover:bg-white/30"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Lili Logo - Top Center */}
        <div className="flex flex-col items-center">
          <div className="w-14 h-14 flex items-center justify-center mb-2 animate-pulse">
            <img 
              src="https://customer-assets.emergentagent.com/job_luxury-collective/artifacts/g86n9tir_Lili%20Logo.1-cutout.png" 
              alt="Lili Logo" 
              className="w-14 h-14 object-contain"
            />
          </div>
          <h3 className="font-bold text-lg">Lili</h3>
          <p className="text-xs text-white/90">AI Concierge • Online</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-col overflow-y-auto p-4 bg-gray-50 space-y-4" style={{ height: 'calc(85vh - 220px)', maxHeight: '350px' }}>
        {messages.map((msg, idx) => (
          <div key={idx}>
            <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.type === 'user' 
                  ? 'text-black' 
                  : 'bg-white border border-gray-200 text-gray-800'
              }`} style={msg.type === 'user' ? { background: 'linear-gradient(to right, #D4AF37, #C5A028)' } : {}}>
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                <p className="text-xs mt-1 opacity-60">
                  {new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
            
            {/* Consent buttons */}
            {msg.requiresConsent && showConsentPrompt && (
              <div className="flex gap-2 justify-start mt-2">
                <Button
                  onClick={() => handleConsent(true)}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
                  size="sm"
                >
                  Yes, I Consent
                </Button>
                <Button
                  onClick={() => handleConsent(false)}
                  variant="outline"
                  className="border-2 border-gray-300 text-sm font-semibold"
                  size="sm"
                >
                  No, Thanks
                </Button>
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#D4AF37' }} />
                <span className="text-sm text-gray-600">Lili is typing...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t-2 border-gray-200">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={showConsentPrompt ? "Please respond to consent first..." : "Type your message..."}
            disabled={isLoading || showConsentPrompt}
            className="flex-1 text-sm border-2"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || showConsentPrompt}
            style={{ background: 'linear-gradient(to right, #D4AF37, #C5A028)' }}
            className="hover:opacity-90 text-white font-bold"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center font-medium">
          Powered by AI • Responses may take a moment
        </p>
      </div>
    </div>
  );
};

export default LiliChatWindow;
